import { Link } from "react-router-dom";
import { Play, Heart, Check } from "lucide-react";
import { Cover } from "@/components/Cover";
import { usePlayer } from "@/context/PlayerContext";
import { api } from "@/lib/api";

const SelectMark = ({ on }) => (
  <span className={`absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full border-2 shadow transition-colors ${on ? "border-zinc-900 bg-zinc-900 text-white" : "border-white bg-white/70 text-transparent"}`}>
    <Check size={13} strokeWidth={3} />
  </span>
);

export const AlbumCard = ({ album, index = 0, selectMode = false, selected = false, onSelect }) => {
  const { playTracks } = usePlayer();
  const play = async (e) => {
    e.preventDefault();
    const { data } = await api.get(`/albums/${album.id}`);
    playTracks(data.tracks);
  };
  const click = (e) => { if (selectMode) { e.preventDefault(); onSelect?.(album.id, e.shiftKey); } };
  const artistTo = `/artist?name=${encodeURIComponent(album.artist || "")}`;
  return (
    <div className="group animate-rise" style={{ animationDelay: `${Math.min(index, 20) * 30}ms` }}>
      <Link to={`/album/${album.id}`} onClick={click} data-testid={`album-card-${album.id}`} data-sel-id={album.id} aria-pressed={selectMode ? selected : undefined} className="block">
        <div className={`relative aspect-square overflow-hidden rounded-md border bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] ${selected ? "border-zinc-900 ring-2 ring-zinc-900" : "border-zinc-200/70"}`}>
          <Cover albumId={album.id} hasCover={album.has_cover} version={album.cover_version} alt={album.name} rounded="rounded-none"
            className={`h-full w-full transition-transform duration-500 ease-out ${selectMode ? (selected ? "scale-[0.96]" : "") : "group-hover:scale-[1.04]"}`} iconSize={40} />
          {selectMode ? <SelectMark on={selected} /> : (
            <button onClick={play} data-testid={`album-card-play-${album.id}`}
              className="absolute bottom-3 right-3 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-zinc-900 text-white opacity-0 shadow-lg transition-[opacity,transform] duration-200 hover:scale-105 group-hover:translate-y-0 group-hover:opacity-100">
              <Play size={16} fill="currentColor" className="ml-0.5" />
            </button>
          )}
          {album.starred && !selectMode && <Heart size={14} fill="currentColor" className="absolute left-3 top-3 text-amber-500 drop-shadow" />}
        </div>
        <p className="mt-3 truncate text-sm font-medium text-zinc-900">{album.name}</p>
      </Link>
      <p className="flex min-w-0 items-baseline text-xs text-zinc-500">
        {selectMode || !album.artist
          ? <span className="truncate">{album.artist}</span>
          : <Link to={artistTo} data-testid={`album-card-artist-${album.id}`} title={`Go to ${album.artist}`}
            className="truncate underline-offset-4 transition-colors hover:text-zinc-900 hover:underline">{album.artist}</Link>}
        {album.year ? <span className="shrink-0 font-mono">&nbsp;· {album.year}</span> : null}
      </p>
    </div>
  );
};

export const AlbumGrid = ({ albums, selectMode = false, selected, onSelect }) => (
  <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6" data-testid="album-grid">
    {albums.map((a, i) => <AlbumCard key={a.id} album={a} index={i} selectMode={selectMode} selected={selected?.has(a.id)} onSelect={onSelect} />)}
  </div>
);
